import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { pollBillingState } from '~/lib/billingRefresh';
import { $planWelcome, beginPlanActivation, celebratePlan, dismissPlanWelcome } from '~/lib/stores/planWelcome';

/**
 * Handles the redirect back from hosted checkout (Stripe/Dodo) and the billing portal:
 *  - Subscription checkout → `?checkout=success|cancel`
 *  - Credit top-up / pack  → `?credits=purchased`
 *  - Domain purchase       → `?domains=purchased`
 *  - Billing Portal return → `?billing=updated`
 *
 * Entitlements are applied server-side by webhooks (never trusted from the redirect),
 * and that grant lands a few seconds AFTER the redirect. `pollBillingState` re-syncs on
 * a bounded backoff until it converges, fanning the fresh state out to every consumer —
 * the sidebar balance/plan (via stores) AND the billing tabs (via react-query) — so all
 * of them update live, no page refresh. The param is stripped so a later refresh doesn't
 * re-toast or re-poll.
 */
export function useCheckoutReturn(): void {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkout = params.get('checkout');
    const billing = params.get('billing');
    const credits = params.get('credits');
    const domains = params.get('domains');

    if (!checkout && !billing && !credits && !domains) {
      return undefined;
    }

    let cleanup: (() => void) | undefined;

    if (checkout === 'success') {
      // Acknowledge the payment IMMEDIATELY with a "confirming" modal, poll in the
      // background, and flip it to the celebration the moment the webhook activates the
      // plan (~3–5s). If it's unusually slow, fall back to a reassuring toast so the
      // user is never stuck staring at a spinner — the background poll still converges.
      beginPlanActivation();

      const stopPoll = pollBillingState(queryClient, {
        onSubscribed: (o) => {
          const plan = o.accountPlan;

          // Plus/Pro sit on the shared plan columns, so the website tier stays 'free':
          // celebrate the plan behind the balance, with the server's allotment
          if (plan && (plan.product === 'code' || plan.product === 'work')) {
            celebratePlan(plan.tier, plan.creditsAllocated);
          } else {
            celebratePlan(o.tier, o.creditsAllocated, o.interval);
          }
        },
      });

      const slowFallback = window.setTimeout(() => {
        if ($planWelcome.get()?.status === 'confirming') {
          dismissPlanWelcome();
          toast.success('Payment received — your plan will activate in a moment.');
        }
      }, 20_000);

      cleanup = () => {
        stopPoll();
        window.clearTimeout(slowFallback);
      };
    } else if (checkout === 'cancel') {
      toast('Checkout canceled.');
    } else if (credits === 'purchased') {
      toast.success('Payment received — your credits will appear shortly.');
      cleanup = pollBillingState(queryClient);
    } else if (domains === 'purchased') {
      toast.success('Purchase received — setting up your domain.');
      cleanup = pollBillingState(queryClient);
    } else if (billing === 'updated') {
      toast.success('Billing updated.');
      cleanup = pollBillingState(queryClient);
    }

    const next = new URLSearchParams(params);
    next.delete('checkout');
    next.delete('billing');
    next.delete('credits');
    next.delete('domains');
    setParams(next, { replace: true });

    return () => cleanup?.();
  }, [params, queryClient, setParams]);
}
